/**
 * Lo que cuesta un producto, donde se sepa.
 *
 * The till read a product's cost only at its own location. Stock received into the storeroom has
 * no cost at the counter until it is moved there, so any price cut passed the margin check and the
 * sale booked its cost at nothing. Now the cost is taken wherever it is known: the till's location
 * first, then wherever else the shop holds the product (the location holding the most, as the
 * reducer does), then what it cost the last time it was bought.
 */

import { ZERO_COST, unitCostFromTotal, type UnitCost } from '@abastece/shared';
import type { Compra, Existencia } from '@abastece/core';

export function costoConocido(
  productoId: string,
  ubicacion: string,
  existencias: readonly Existencia[],
  compras: readonly Compra[] = [],
): UnitCost {
  const aqui = existencias.find((e) => e.productoId === productoId && e.ubicacion === ubicacion);
  if (aqui && aqui.costoPromedio > 0) return aqui.costoPromedio;
  const otro = existencias
    .filter((e) => e.productoId === productoId && e.costoPromedio > 0)
    .sort((a, b) => b.cantidad - a.cantidad)[0];
  if (otro) return otro.costoPromedio;
  const ultima = compras
    .filter((c) => !c.anulada && c.lineas.some((l) => l.productoId === productoId && l.cantidadBase > 0))
    .sort((a, b) => b.fecha.localeCompare(a.fecha))[0];
  const linea = ultima?.lineas.find((l) => l.productoId === productoId && l.cantidadBase > 0);
  return linea ? unitCostFromTotal(linea.costoCordobas, linea.cantidadBase) : ZERO_COST;
}

/** How far off a cost may be from what the product has been costing before a purchase line says so. */
export const DESVIO_AVISO = 0.3;

/**
 * A new cost against the one on record, as a fraction: 0.45 is 45% more, −0.5 half. Null with
 * nothing on record to compare against.
 */
export function desvio(nuevo: UnitCost, referencia: UnitCost): number | null {
  return referencia > 0 ? (nuevo - referencia) / referencia : null;
}

/**
 * What `calcular` comes to, or null when it is past what the books take (C$ 100,000,000,
 * `centavos`). Such a figure comes from a quantity or an amount nobody has, and a field holds
 * one for as long as a USB scanner is typing a code into it, until `lib/lector-usb.ts` takes the
 * code back out. Somebody can type one too. Worked out while a screen draws, it threw, and the
 * screen went down with it (a transfer, 2026-10-02).
 */
export function enRango<T>(calcular: () => T): T | null {
  try {
    return calcular();
  } catch (e) {
    if (e instanceof RangeError) return null;
    throw e;
  }
}

/**
 * The same supplier's same invoice already entered and not voided: the second time is how one
 * delivery gets paid twice. Compared on letters and digits, as a receipt's reference is.
 */
export function compraRepetida(
  compras: readonly Compra[],
  proveedorId: string,
  documento: string,
): Compra | undefined {
  const limpio = (d: string) => d.replace(/[^0-9a-z]/gi, '').toUpperCase();
  const buscado = limpio(documento);
  if (!buscado) return undefined;
  return compras.find((c) => !c.anulada && c.proveedorId === proveedorId && c.documento && limpio(c.documento) === buscado);
}
